import React, { useEffect, useMemo, useState } from 'react'
import DropdownInput, { DropdownOption } from './DropdownInput'
import trainService, { TrainProjectNode as TrNode } from '../services/trainService'

// 표준값의 단일 출처는 백엔드(WORK_CATEGORIES) — API 로 받아오고,
// 이 상수는 API 실패 시 폴백으로만 쓴다.
type CategoryOption = { name: string; description: string }
const FALLBACK_WORK_CATEGORIES: CategoryOption[] = [
  { name: '환경 인지', description: '농경지 인식 모델' },
  { name: '객체 인지', description: '장애물 감지 모델' },
  { name: '작업 관리', description: '로더 감지 모델, 작업기 감지 모델, 작업기 분류 모델' },
]
type WorkCategory = string

interface Props {
  isOpen: boolean
  onClose: () => void
  onCreated?: (ids: { project_id: number; task_id?: number }) => void | Promise<void>
}

export default function CreateTrainNodesModal({ isOpen, onClose, onCreated }: Props) {
  const MAX_DESC = 1000
  const [nodes, setNodes] = useState<TrNode[]>([])

  const [selProject, setSelProject] = useState<TrNode | null>(null)
  const [projectName, setProjectName] = useState('')
  const [projectDescription, setProjectDescription] = useState('')
  const [workCategory, setWorkCategory] = useState<WorkCategory | ''>('')
  const [categories, setCategories] = useState<CategoryOption[]>(FALLBACK_WORK_CATEGORIES)

  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  type ReqField = { key: string; input_type: 'int'|'float'|'string'|'text' }
  const defaultReqFields: ReqField[] = useMemo(() => (
    [ { key: 'Score', input_type: 'float' }, { key: 'Model architecture', input_type: 'string' } ]
  ), [])
  const [reqFields, setReqFields] = useState<ReqField[]>(defaultReqFields)
  const [newFieldKey, setNewFieldKey] = useState('')
  const [newFieldType, setNewFieldType] = useState<ReqField['input_type']>('float')

  const isValid = useMemo(() => {
    const hasProject = !!selProject || projectName.trim().length > 0
    return hasProject && workCategory.length > 0
  }, [selProject, projectName, workCategory])

  useEffect(() => {
    if (!isOpen) return
    ;(async () => {
      try { const list = await trainService.listProjects(); setNodes(list) } catch { setNodes([]) }
      try {
        const cats = await trainService.getTaskCategories()
        if (cats.length > 0) setCategories(cats)
      } catch { setCategories(FALLBACK_WORK_CATEGORIES) }
      setSelProject(null)
      setProjectName(''); setProjectDescription('')
      setWorkCategory('')
      setReqFields(defaultReqFields)
      setNewFieldKey(''); setNewFieldType('float')
      setError(null)
    })()
  }, [isOpen, defaultReqFields])

  // 모달 열림 시 배경 스크롤 방지
  useEffect(() => {
    if (!isOpen) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [isOpen])

  if (!isOpen) return null

  const projectOptions: DropdownOption[] = nodes
    .filter(n => n.kind === 'project' && (n.parent_id == null))
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map(n => ({ id: n.id, name: n.name, description: n.description || '' }))

  const addReqField = () => {
    const raw = (newFieldKey || '').trim()
    if (!raw) return
    const exists = reqFields.some(f => (f.key || '').trim().toLowerCase() === raw.toLowerCase())
    if (exists) { setError(`'${raw}' 항목은 이미 추가되어 있습니다.`); return }
    // 중복이 아니면 에러 메시지 제거
    if (error) setError(null)
    setReqFields(prev => [...prev, { key: raw, input_type: newFieldType }])
    setNewFieldKey('')
    setNewFieldType('float')
  }
  const removeReqField = (idx: number) => {
    setReqFields(prev => prev.filter((_, i) => i !== idx))
  }

  const handleSubmit = async () => {
    if (!isValid) { setError('프로젝트명과 작업 카테고리는 모두 필수입니다.'); return }
    setError(null)
    try {
      setSubmitting(true)
      // 1) 프로젝트 결정/생성
      let projectId: number
      if (selProject) {
        projectId = selProject.id
      } else {
        const r = await trainService.createProject(projectName.trim(), projectDescription.trim() || undefined)
        projectId = r.project_id
      }
      // 2) 프로젝트 안에 같은 작업 카테고리가 있으면 재사용하고, 없으면 생성
      const existingCategory = nodes.find(
        n => n.kind === 'task' && n.parent_id === projectId && n.name === workCategory
      )
      let taskId: number | undefined = existingCategory?.id
      if (!taskId) {
        const r2 = await trainService.createTask(projectId, workCategory, undefined, reqFields)
        taskId = r2.task_id
      }
      if (onCreated) await onCreated({ project_id: projectId, task_id: taskId })
      setSubmitting(false)
      onClose()
    } catch (e: any) {
      setSubmitting(false)
      setError(e?.message || '생성 중 오류가 발생했습니다.')
    }
  }

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md mx-4 h-[80vh] flex flex-col overflow-hidden shadow-lg">
        <div className="text-lg font-semibold mb-2 shrink-0">프로젝트/작업 카테고리 생성</div>
        <div className="text-xs text-gray-500 mb-4 shrink-0">프로젝트를 선택하거나 새로 만든 뒤, 모델에 적용할 작업 카테고리를 선택하세요.</div>

        <div className="space-y-4 flex-1 min-h-0">
          {/* 프로젝트 */}
          <div>
            <DropdownInput
              label={<>프로젝트명 <span className="text-red-500 ml-0.5">*</span></>}
              value={projectName}
              onChange={setProjectName}
              options={projectOptions}
              placeholder={"예: TG320 농경지 탐지"}
              inputDisabled={!!selProject || submitting}
              dropdownDisabled={submitting}
              onPickExisting={(opt) => {
                const node = nodes.find(n => n.kind === 'project' && n.id === Number(opt.id)) || null
                setSelProject(node)
                setProjectName(opt.name)
                setProjectDescription((node?.description as any) || '')
                // 프로젝트 변경 시 하위 초기화
                setWorkCategory('')
                setReqFields(defaultReqFields)
              }}
              onDirectInput={() => {
                setSelProject(null)
                setProjectName('')
                setProjectDescription('')
                setWorkCategory('')
                setReqFields(defaultReqFields)
              }}
            />
            <label className="block text-sm text-gray-800 mb-1 mt-2">프로젝트 설명 (선택)</label>
            <textarea
              value={selProject ? ((selProject?.description as any) || '') : (projectDescription || '')}
              onChange={(e) => { if (!selProject) setProjectDescription(e.target.value) }}
              className={`w-full border rounded px-3 py-2 text-sm ${selProject ? 'border-gray-200 bg-gray-50 text-gray-600 opacity-60 cursor-not-allowed' : 'border-gray-300'}`}
              rows={2}
              placeholder="예: YOLOv8 기반 작물 경계 탐지 프로젝트"
              readOnly={!!selProject}
              aria-disabled={!!selProject}
              maxLength={MAX_DESC}
            />
          </div>

          {/* 작업 카테고리 */}
          <div>
            <div className="block text-sm text-gray-800 mb-2">
              작업 카테고리 <span className="text-red-500 ml-0.5">*</span>
            </div>
            <select
              value={workCategory}
              disabled={submitting}
              aria-label="작업 카테고리"
              className={`w-full border border-gray-300 rounded px-3 py-2 text-sm bg-white text-gray-900 ${submitting ? 'opacity-60 cursor-not-allowed' : ''}`}
              onChange={async (e) => {
                const category = e.target.value
                setWorkCategory(category)
                // 선택한 카테고리가 프로젝트에 이미 있으면 그 하이퍼파라미터 스키마를 불러온다
                const existingCategory = category && selProject
                  ? nodes.find(n => n.kind === 'task' && n.parent_id === selProject.id && n.name === category)
                  : undefined
                if (!existingCategory) {
                  setReqFields(defaultReqFields)
                  return
                }
                try {
                  const t = await trainService.getTask(existingCategory.id)
                  setReqFields((t?.required_fields as any) || defaultReqFields)
                } catch { setReqFields(defaultReqFields) }
              }}
            >
              {/* hidden: 접힌 상태의 placeholder 로만 쓰이고 펼친 목록에는 나오지 않는다 */}
              <option value="" disabled hidden>작업 카테고리 선택</option>
              {categories.map(category => (
                <option key={category.name} value={category.name}>
                  {category.description ? `${category.name} — ${category.description}` : category.name}
                </option>
              ))}
            </select>
          </div>

          {/* 필수항목 설정 */}
          <div className="border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 text-sm font-medium text-gray-800">필수항목 설정</div>
              <div className="p-3 flex flex-col gap-3 h-[250px] overflow-hidden">
              <div className="grid grid-cols-4 gap-2">
                <input
                  className="col-span-2 border rounded px-2 py-1 text-sm"
                  placeholder="키 (예: mAP, F1 Score)"
                  value={newFieldKey}
                  onChange={e => setNewFieldKey(e.target.value)}
                />
                <div className="flex gap-2 col-span-2">
                  <select className="border rounded px-2 py-1 text-sm flex-1" value={newFieldType} onChange={e => setNewFieldType(e.target.value as any)}>
                    <option value="int">int</option>
                    <option value="float">float</option>
                    <option value="string">string</option>
                    <option value="text">text</option>
                  </select>
                  <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50 shrink-0 whitespace-nowrap" onClick={addReqField}>추가</button>
                </div>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto border rounded">
                {reqFields.length === 0 ? (
                  <div className="px-3 py-2 text-sm text-gray-500">항목이 없습니다. 위에서 추가하세요.</div>
                ) : (
                  <ul className="divide-y">
                    {reqFields.map((f, idx) => (
                      <li key={`${f.key}-${idx}`} className="flex items-center justify-between px-3 py-2 text-sm">
                        <div className="text-gray-800">
                          <span className="font-medium">{f.key}</span>
                          <span className="ml-2 text-gray-500">({f.input_type})</span>
                        </div>
                        <button className="text-xs text-red-600 hover:underline" onClick={() => removeReqField(idx)}>삭제</button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
              
            </div>
            
          </div>
        </div>

        {error && <div className="px-3 text-xs text-red-600">{error}</div>}
      

        <div className="mt-2 flex justify-end gap-2 shrink-0 pt-3 border-t border-gray-200">
          <button className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50" onClick={onClose} disabled={submitting}>취소</button>
          <button
            className={`px-3 py-1 text-sm rounded text-white ${isValid ? 'bg-gray-800 hover:bg-gray-900' : 'bg-gray-400 cursor-not-allowed'}`}
            disabled={!isValid || submitting}
            onClick={handleSubmit}
          >{submitting ? '생성 중...' : '생성'}</button>
        </div>
      </div>
    </div>
  )
}

